import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api } from "../../lib/api";

const EMPTY = { name: "", address: "", url: "", note: "", is_warehouse: false };

export default function StoresDialog({ open, onClose, onChanged }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // store id or "new"
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get("/inventory-stores");
      setStores(r.data || []);
    } catch {
      toast.error("Eroare la încărcarea magaziilor");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [open]);

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name || "",
      address: s.address || "",
      url: s.url || "",
      note: s.note || "",
      is_warehouse: !!s.is_warehouse,
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Numele e obligatoriu"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim() || null,
        url: form.url.trim() || null,
        note: form.note.trim() || null,
        is_warehouse: form.is_warehouse,
      };
      if (editing === "new") {
        await api.post("/inventory-stores", payload);
        toast.success("✓ Magazie creată");
      } else {
        await api.put(`/inventory-stores/${editing}`, payload);
        toast.success("✓ Magazie actualizată");
      }
      cancelEdit();
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Șterge magazia „${s.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/inventory-stores/${s.id}`);
      toast.success("Magazie ștearsă");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stores-dialog">
        <DialogHeader>
          <DialogTitle>🏪 Gestionează magazii / depozite</DialogTitle>
        </DialogHeader>

        <div className="text-[12px] text-zinc-500 italic mb-2">
          Marchează o magazie ca <strong>depozit principal</strong> — adresa ei va fi folosită automat ca destinație de livrare pentru comenzile noi.
        </div>

        {/* Edit / Create form */}
        {editing && (
          <div className="border-2 border-emerald-300 bg-emerald-50/50 rounded p-3 mb-3 space-y-2">
            <div className="text-[11px] uppercase font-bold text-emerald-800">
              {editing === "new" ? "➕ Magazie nouă" : "✏ Editare magazie"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="small-label">Nume *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex: Depozit central Zürich"
                  data-testid="store-name"
                />
              </div>
              <div>
                <Label className="small-label">URL (opțional)</Label>
                <Input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://..."
                  data-testid="store-url"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="small-label">Adresă (folosită ca destinație livrare)</Label>
                <Textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="ex: Industriestrasse 12, 8005 Zürich"
                  className="min-h-[60px] text-sm"
                  data-testid="store-address"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="small-label">Notă (opțional)</Label>
                <Textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="min-h-[40px] text-sm"
                  data-testid="store-note"
                />
              </div>
              <label className="md:col-span-2 inline-flex items-center gap-2 cursor-pointer select-none mt-1">
                <input
                  type="checkbox"
                  checked={form.is_warehouse}
                  onChange={(e) => setForm({ ...form, is_warehouse: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="store-is-warehouse"
                />
                <span className="text-sm font-semibold text-zinc-900">
                  🏭 Depozit principal (destinația default a comenzilor noi)
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={cancelEdit} disabled={busy}>Anulează</Button>
              <Button
                size="sm"
                onClick={save}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="store-save"
              >
                {busy ? "Se salvează…" : "✓ Salvează"}
              </Button>
            </div>
          </div>
        )}

        {!editing && (
          <div className="mb-2 flex justify-end">
            <Button size="sm" onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="store-add-new">
              ➕ Adaugă magazie
            </Button>
          </div>
        )}

        {/* List */}
        <div className="space-y-2" data-testid="stores-list">
          {loading ? (
            <div className="text-center text-sm text-zinc-400 py-6">Se încarcă…</div>
          ) : stores.length === 0 ? (
            <div className="text-center text-sm text-zinc-400 italic py-6">Nicio magazie configurată.</div>
          ) : (
            stores.map((s) => (
              <div
                key={s.id}
                className={`border rounded-md p-3 flex items-start justify-between gap-2 ${s.is_warehouse ? "border-emerald-400 bg-emerald-50" : "border-zinc-200 bg-white"}`}
                data-testid={`store-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-900 flex items-center gap-1.5">
                    🏪 {s.name}
                    {s.is_warehouse && (
                      <span className="px-1.5 py-0.5 bg-emerald-600 text-white text-[9px] uppercase tracking-wider rounded font-bold">
                        Depozit principal
                      </span>
                    )}
                  </div>
                  {s.address && <div className="text-[12px] text-zinc-600 mt-0.5">📍 {s.address}</div>}
                  {s.note && <div className="text-[11px] italic text-zinc-500 mt-0.5">{s.note}</div>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => startEdit(s)} data-testid={`store-edit-${s.id}`}>
                    ✏
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50" onClick={() => remove(s)} data-testid={`store-delete-${s.id}`}>
                    🗑
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
